import { useQuery } from "@tanstack/react-query";
import { xano } from "@/lib/xano";
import { useCompany } from "@/lib/company";
import type { OverviewPayload, AiSummary } from "@/lib/engine-types";
import { PageHeader, StatCard, LoadingState, ErrorState, EmptyState } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Sparkles, CheckCircle2, XCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { num, dateUk } from "@/lib/format";
import { ComplianceCalendar } from "@/components/ComplianceCalendar";

function statusIcon(status: string) {
  if (status === "pass") return <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />;
  if (status === "warn") return <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />;
  return <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />;
}

export default function Overview() {
  const { activeToken, tokenLoading } = useCompany();

  const overview = useQuery({
    queryKey: ["overview", activeToken],
    queryFn: () => xano.dashboard.overview(activeToken!) as Promise<OverviewPayload>,
    enabled: !!activeToken,
  });

  const ai = useQuery({
    queryKey: ["ai-summary", activeToken],
    queryFn: () => xano.dashboard.aiSummary(activeToken!) as Promise<AiSummary>,
    enabled: !!activeToken,
  });

  if (!activeToken && !tokenLoading) {
    return (
      <>
        <PageHeader title="Overview" />
        <EmptyState
          icon={Building2}
          title="No company yet"
          description="Add your company to see your trade footprint, VAT position and AI briefing."
        />
      </>
    );
  }

  if (tokenLoading || overview.isLoading) {
    return (
      <>
        <PageHeader title="Overview" />
        <LoadingState label="Loading your dashboard…" />
      </>
    );
  }
  if (overview.isError || !overview.data?.company) {
    return (
      <>
        <PageHeader title="Overview" />
        <ErrorState
          error={
            overview.error ??
            new Error("This company has not finished syncing yet. Connect a store to populate it.")
          }
        />
      </>
    );
  }

  const d = overview.data;
  // Correct the Shopify-plan fix at the render layer: Shopify opened native
  // Duties & Import Taxes to ALL plans (incl. Grow) on 2 Feb 2025, so it is no
  // longer a plan blocker. The engine (/dashboard) still hard-codes it as a fail
  // and can't be edited via MCP right now, so we patch it here.
  const fixes = (d.recommendations?.fixes ?? []).map((f) =>
    f.key === "shopify_plan"
      ? {
          ...f,
          status: "warn",
          title: "Duties at checkout: available on your plan",
          text: "Native Duties & Import Taxes now works on every Shopify plan (incl. Grow) since Feb 2025, no upgrade needed. Switch it on with HS codes on your products (we supply these) and a DDP-capable carrier; a 0.5% fee applies.",
        }
      : f
  );
  const fixesFailing = fixes.filter((f) => f.status === "fail").length;
  const cta = d.recommendations?.cta;

  return (
    <>
      <PageHeader
        title="Overview"
        description={`${d.company.name} — your import / export position at a glance`}
      />

      {/* Headline numbers */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Products" value={num(d.products?.length ?? 0)} hint="In your catalogue" />
        <StatCard
          label="Trade codes"
          value={num(d.trade_activity?.hs6_codes?.length ?? 0)}
          hint="Active HS6 codes (HMRC)"
        />
        <StatCard
          label="Markets tracked"
          value={num(d.market_snapshots?.length ?? 0)}
          hint="Export markets with intel"
        />
        <StatCard
          label="To fix"
          value={num(fixesFailing)}
          tone={fixesFailing > 0 ? "warning" : "success"}
          hint="Compliance gaps found"
        />
      </div>

      {/* Accountant insight — the "how do you know that" moment */}
      {d.accountant?.matched && (
        <Card className="mb-6 border-primary/30 bg-accent/40">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="font-medium">
                  Your current accountant looks like {d.accountant.name}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{d.accountant.insight}</p>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    Registered office: <strong>{d.accountant.registered_office_postcode}</strong>
                  </span>
                  <span>
                    Operating from: <strong>{d.accountant.operating_postcode}</strong>
                  </span>
                  {d.accountant.client_count ? (
                    <span>
                      {num(d.accountant.client_count)} companies at that office
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* AI briefing */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <h2 className="text-sm font-semibold">AI briefing</h2>
              {ai.data?.cached && <Badge variant="secondary">cached</Badge>}
            </div>
            {ai.isLoading ? (
              <div className="animate-pulse text-sm text-muted-foreground">Generating…</div>
            ) : ai.isError ? (
              <p className="text-sm text-muted-foreground">Briefing unavailable right now.</p>
            ) : (
              <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
                {ai.data?.summary}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Company facts */}
        <Card>
          <CardContent className="space-y-3 p-5 text-sm">
            <h2 className="text-sm font-semibold">Company</h2>
            <Row label="Registration" value={d.company.registration_number || "—"} />
            <Row
              label="VAT"
              value={
                d.company.vat_registered ? (
                  <Badge variant="success">Registered</Badge>
                ) : (
                  <Badge variant="secondary">Not registered</Badge>
                )
              }
            />
            <Row label="Next accounts due" value={dateUk(d.company.next_accounts_due)} />
            <Row
              label="Confirmation statement"
              value={dateUk(d.company.next_confirmation_statement_due)}
            />
            {d.trade_activity?.found && (
              <>
                <Row label="HMRC trader ID" value={num(d.trade_activity.trader_id)} />
                <Row label="Operating from" value={d.trade_activity.operating_postcode || "—"} />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Compliance calendar */}
      <section className="mt-6">
        <ComplianceCalendar />
      </section>

      {/* What to fix */}
      {fixes.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 text-sm font-semibold text-muted-foreground">What to fix</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {fixes.map((f) => (
              <Card key={f.key}>
                <CardContent className="flex items-start gap-3 p-4">
                  {statusIcon(f.status)}
                  <div>
                    <div className="text-sm font-medium">{f.title}</div>
                    <p className="mt-0.5 text-sm text-muted-foreground">{f.text}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Next 30 days + CTA */}
      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {d.recommendations?.next_30_days?.length > 0 && (
          <Card className="lg:col-span-2">
            <CardContent className="p-5">
              <h2 className="mb-3 text-sm font-semibold">Recommended next 30 days</h2>
              <ol className="space-y-2">
                {d.recommendations.next_30_days.map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-semibold">
                      {i + 1}
                    </span>
                    {step}
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}
        {cta && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="flex h-full flex-col justify-between gap-3 p-5">
              <div>
                <div className="font-display text-lg font-semibold">
                  {typeof cta === "object" ? cta.title : "Get goforma running this"}
                </div>
                {typeof cta === "object" && cta.text && (
                  <p className="mt-1 text-sm text-muted-foreground">{cta.text}</p>
                )}
              </div>
              <button className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
                {typeof cta === "object" && cta.price ? cta.price : "Talk to us"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}
